import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import dayjs from "dayjs";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};

export default function LeaveOverlay({ loading, data }) {
  const byDay   = data?.byDay   || [];
  const clashes = data?.clashes || [];

  // Colour bars by threshold — high leave = yellow/red
  const max = Math.max(1, ...byDay.map(d => d.count || 0));
  const chartData = byDay.map(d => ({
    date:  dayjs(d.date).format("DD MMM"),
    count: d.count || 0,
    fill:  d.count / max >= 0.75 ? "#f87171"
         : d.count / max >= 0.5  ? "#fbbf24"
         : "#00ff90",
  }));

  if (loading) return (
    <div className={`${card} p-5`}>
      <div className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Leave Overlay</div>
      <div className="h-48 rounded-lg bg-white/5 animate-pulse" />
    </div>
  );

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Leave Overlay — Next 14 Days</span>
        {clashes.length > 0 && (
          <span className="text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 px-2 py-0.5 rounded-full">
            {clashes.length} capacity warning{clashes.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div className="p-5 space-y-4">
        {byDay.length === 0 ? (
          <p className="text-white/20 text-sm italic text-center py-4">No leave data available.</p>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={120}>
              <BarChart data={chartData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.3)" }} />
                <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} allowDecimals={false} />
                <Tooltip {...tooltipStyle} formatter={v => [`${v} on leave`, "Count"]} />
                <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                  {chartData.map((d, i) => <Cell key={i} fill={d.fill} fillOpacity={0.75} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Capacity warnings */}
            {clashes.length > 0 && (
              <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/5 p-3">
                <p className="text-xs text-yellow-400/60 uppercase tracking-wider mb-2">Capacity Warnings</p>
                <ul className="space-y-1">
                  {clashes.map((c, i) => (
                    <li key={i} className="text-xs text-yellow-400/70">
                      <span className="font-medium">{c.dept}</span> on {dayjs(c.date).format("DD MMM")}: {c.count} on leave
                      {c.threshold && <span className="text-yellow-400/40"> (threshold: {c.threshold})</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}